import { FaRegUserCircle } from 'react-icons/fa';
import Image from 'next/image';
import { Message } from 'ai';
import { format } from 'date-fns';
import { Source } from '@/app/chat/ChatWindow';
import { StructuredOutput } from '@/app/chat/StructuredOutput';
import { cn } from '@/utils/uiUtils';
import { formatTime, formatDateTime } from '@/utils/timeUtils';
import { useSettingsStore } from '@/app/stores/settingsStore';
import { useUser } from '@clerk/nextjs';

export const TRAINME_DEFAULT_COACH_AVATAR_URL =
  'https://tpniqeccmqpshgkgshtb.supabase.co/storage/v1/object/public/coach-images/trainme-default-avatar.png';

interface ChatMessageBubbleProps {
  message: Message;
  sources?: Source[];
  onAction?: (action: string) => void;
}

export function ChatMessageBubble(props: ChatMessageBubbleProps) {
  const { message, sources, onAction } = props;
  const alignmentClassName =
    message.role === 'user'
      ? 'ml-auto bg-slate-200 dark:bg-slate-800 w-2/3'
      : 'mr-auto bg-slate-200 dark:bg-slate-800 w-11/12';
  const { user } = useUser();
  const coachProfile = useSettingsStore(
    (state) => state.trainingSettings?.coachProfile
  );
  const createdAtDate =
    message.role === 'assistant' && message.createdAt
      ? new Date(message.createdAt)
      : null;
  const createdAtShortDate =
    createdAtDate && !isNaN(createdAtDate.getTime())
      ? format(createdAtDate, 'MM dd')
      : '';

  const avatar =
    message.role === 'user' ? (
      user?.imageUrl ? (
        <Image
          src={user.imageUrl}
          className='w-5 h-5 md:w-8 md:h-8 rounded-full object-cover'
          alt='User'
          width={24}
          height={24}
        />
      ) : (
        <FaRegUserCircle className='w-5 h-5 md:w-8 md:h-8 rounded-full' />
      )
    ) : (
      <Image
        src={coachProfile?.avatarUrl ?? TRAINME_DEFAULT_COACH_AVATAR_URL}
        className='w-5 h-5 md:w-8 md:h-8 rounded-full object-cover'
        alt={coachProfile?.name ?? 'AI Coach'}
        width={32}
        height={32}
      />
    );

  return (
    <div className={cn(['chat-message-bubble', alignmentClassName])}>
      <div
        className='mr-3 self-start flex flex-col items-center gap-0.5'
        title={formatDateTime(message.createdAt)}
      >
        {avatar}
        {message.role === 'assistant' && (
          <div className='mt-1 flex flex-col items-center leading-none text-slate-400 dark:text-slate-500'>
            <span className='text-[11px]'>{createdAtShortDate}</span>
            <span className='text-[10px] mt-0.5'>
              {formatTime(message.createdAt)}
            </span>
          </div>
        )}
      </div>
      <div className='w-full flex flex-col prose prose-sm md:prose-base text-slate-900 dark:text-slate-100 leading-relaxed md:leading-normal'>
        <StructuredOutput {...props} onAction={onAction} />
      </div>
      {sources && sources.length > 0 && (
        <>
          <code className='mt-4 mr-auto bg-slate-600 px-2 py-1 rounded'>
            <h2 className='h2'>🔍 Sources:</h2>
          </code>
          <code className='mt-1 mr-2 bg-slate-600 px-2 py-1 rounded '>
            {props.sources?.map((source, i) => (
              <div className='mt-2' key={source.id || `source-${i}`}>
                {i + 1}. &quot;{source.metadata?.loc?.lines?.from} to{' '}
                {source.metadata?.loc?.lines?.to}&quot;
                {source.metadata?.loc?.lines !== undefined ? (
                  <div>
                    <br />
                    Lines {source.metadata?.loc?.lines?.from} to{' '}
                    {source.metadata?.loc?.lines?.to}
                  </div>
                ) : (
                  ''
                )}
              </div>
            ))}
          </code>
        </>
      )}
    </div>
  );
}
